import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import ReusableTable, { Column } from "../../../shared/ReusableTable/ReusableTable";
import { resetAllFundDetails, setNewFundDetails } from "../../../store/features/auth/newFundSlice";
import { resetTransferFunds } from "../../../store/features/auth/transferFundsSlice";
import { resetImportDataDetails } from "../../../store/Financials/importDataSlice";
import { resetSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { setActiveIndex, setOpenMenuIndex } from "../../../store/MenutItems/menuUiSlice";
import { RootState } from "../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import basestyle from "../../CSS/Base.module.css";
import { getFundSource } from "../services/Funds/FundService";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../StyledComponents/Container";
import { formatCurrency } from "../Utils/formatUtils";
import { notifyUser } from "../Utils/notifyUser";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";


const FundsListing = () => {
    const [count, setCount] = useState(0);
    const [fundList, setFundList] = useState<any[]>([]);

    const participantDetail = useSelector((state: RootState) => state.participantDetails);
    const [participantType, setParticipantType] = useState("");
    const retain = useSelector((state: RootState) => state.table.funds);
    const [fundName, setFundName] = useState(retain?.filters?.fundName ?? "");
    const [status, setStatus] = useState(retain?.filters?.status || constants.IS_Status_ACTIVE);
    const [page, setPage] = useState(retain?.page || 0);
    const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
    const [isLoading, setIsLoading] = useState(false);
    const [clearTrigger, setClearTrigger] = useState(false);
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.auth);

    const ClientDetails = getClientDetailsByClientName(userDetails?.clientVo?.name);
    const LoggedinRoleName: string = userDetails?.roles?.roleName || "";
    const AdminProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const translate = t as (key: string) => string;
    const restrictedRoles = [
        constants.ROLES_PROJECT_APPROVER,
        constants.ROLES_READ_ONLY_USER, ,
        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
        ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : [])
    ];
    const Columns: Column[] = [
        { id: "fundName", label: translate('fundName'), minWidth: 150, align: "left", isHyperlink: true },
        { id: "description", label: translate('fundDescription'), minWidth: 150, align: "left", isHyperlink: false },
        { id: "availableBalance", label: translate('availableBalance'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "isActive", label: translate('status'), minWidth: 80, align: "center", isHyperlink: false },
    ];

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        setPage(0);
        getFundsInfo(fundName, status, 0, rowsPerPage);
    };
    const resetFunds = () => {
        dispatch(resetAllFundDetails());
        dispatch(resetTransferFunds());
    };



    const handleClear = () => {
        setFundName("");
        setPage(0);
        setRowsPerPage(5);
        setStatus(constants.IS_Status_ACTIVE);
        setClearTrigger(prev => !prev);
        dispatch(resetTableState({ tableKey: 'funds' }));
    };
    const handleNewFund = () => {
        if (LoggedinRoleName === constants.ROLES_PARTICIPANT_USER && participantDetail.funds.filter((item: any) => item.isActive === constants.IS_ACTIVE_YES).length >= 10) {
            notifyUser(translate('participantUsersCanCreateUpTo10ActiveFundsOnly'), 'error', translate('fund'));
            resetFunds();
            return;
        } else {
            resetFunds();
            navigate("/enterFundDetails");
        }
    };
    const handleTransferNewFund = () => {
        dispatch(resetImportDataDetails());
        navigate("/fundTransferDetails");
        dispatch(resetSubChartDetails());
    };

    useEffect(() => {
        if (userDetails?.defaultParticipantId) {
            const participant = (participantDetail.participantsArray as { participantId: string; participantType: string }[]).find((item) => item.participantId === userDetails.defaultParticipantId);
            setParticipantType(participant?.participantType ?? "")

        }
    }, [userDetails?.defaultParticipantId]);


    async function getFundsInfo(fundName: string, status: string, pages: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            let isActive = "";
            if (status === constants.IS_Status_ACTIVE) isActive = constants.IS_ACTIVE_YES;
            else if (status === constants.IS_Status_INACTIVE) isActive = constants.IS_ACTIVE_NO;

            const fundSearchVO = {
                participantId: userDetails.defaultParticipantId,
                fundName: fundName,
                isActive,
                page: pages + 1,
                size: rowsPerPage
            };
            dispatch(setTableFilters({ tableKey: 'funds', filters: fundSearchVO }));
            dispatch(setPagination({ tableKey: 'funds', page: pages, pageSize: rowsPerPage }));
            const response = await getFundSource(fundSearchVO);
            if (response?.status === constants.STATUS_CODES.SUCCESS) {
                setCount(response.data?.totalRecordCount);
                setFundList(response.data?.data || []);
            } else {
                setFundList([]);
                setCount(0);
            }
        } catch (error) {
            setFundList([]);
            setCount(0);
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getFundsInfo(fundName, status, page, rowsPerPage);
    }, [clearTrigger, status, page, rowsPerPage]);



    const transformedFundList = fundList.map((item: any) => ({
        ...item,
        availableBalance: item.availableBalance ? formatCurrency(item.availableBalance) : "$0.00",
        isActive: item.isActive === constants.IS_ACTIVE_YES ? constants.IS_Status_ACTIVE : constants.IS_Status_INACTIVE,
    }));

    const handleFundDetailsClick = (rowData: any) => {
        dispatch(
            setNewFundDetails({
                fundId: rowData.fundId,
                page: 0,
                rowsPerPage: 5,
                transactionPage: 0,
                transactionRowsPerPage: 5,
            })
        );
        navigate("/fundSummary", { state: { homePath: "fundsListing" } });
    };

    const handleBack = () => {
        dispatch(setActiveIndex(0));
        dispatch(setOpenMenuIndex(null));
        navigate("/summary");
    };
    console.log('count', count);

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('funds')}</SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomTextField
                                    label={translate('fundName')}
                                    onKeyDown={handleKeyPress}
                                    value={fundName}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setFundName(value);
                                    }}
                                    className="small-text-field"
                                    size="small"
                                    inputProps={{ maxLength: 50 }}
                                />
                                <CustomAutocomplete
                                    sx={{ width: { xs: "100%", sm: 200 } }}
                                    options={["All", constants.IS_Status_ACTIVE, constants.IS_Status_INACTIVE]}
                                    value={status}
                                    onChange={(event, newValue) => {
                                        if (newValue !== null) {
                                            setStatus(newValue);
                                            setPage(0);
                                            setRowsPerPage(5);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!status) {
                                            setStatus(constants.IS_Status_ACTIVE);
                                        }
                                    }}
                                    label={translate('status')}
                                />
                                <ContainedButton onClick={handleSearch}>{translate('search')}</ContainedButton>
                                <OutlinedButton onClick={handleClear}>{translate('clear')}</OutlinedButton>
                            </div>
                            {!restrictedRoles.includes(userDetails?.roles?.roleName ?? "") && <div className="right-fields">

                                <ContainedButton onClick={handleTransferNewFund} >  {translate('transferFund')} </ContainedButton>
                                <ContainedButton onClick={handleNewFund} sx={{ ml: 1 }}>  {translate('newFund')} </ContainedButton>
                            </div>}
                        </div>
                        <ReusableTable
                            tableKey="FundsListing"
                            columns={Columns}
                            rows={transformedFundList}
                            count={count}
                            page={page}
                            participantType={participantType}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(newPage) => setPage(newPage)}
                            onRowsPerPageChange={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
                            loading={isLoading}
                            onRowClick={handleFundDetailsClick}
                        />
                        <Box className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={handleBack}>  {translate('back')}  </ContainedButton>
                        </Box>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default FundsListing;